import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IsoCountryCode } from '@rbilabs/intl-common';
import { useIntl } from 'react-intl';
import { Route, Routes } from 'react-router-dom';

import { BankSelect } from 'components/adyen-bank-select';
import { IAdyenIdealBank } from 'components/adyen-bank-select/types';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import CreditCardFormInputs from 'components/credit-card-form-inputs';
import { AddGiftCardModal, TransferGiftCardModal } from 'components/gift-card-modal';
import { SuccessGiftCardModal } from 'components/gift-card-modal/success-gift-card';
import { SuccessTransferGiftCardModal } from 'components/gift-card-modal/success-transfer-gift-card';
import { MobileSpacer } from 'components/mobile-spacer';
import PaymentMethod from 'components/payment-method';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { getCurrentSelectedMethodType } from 'components/payment-method/utils';
import { ThreeDSChallengeModal } from 'components/three-ds-challenge-modal';
import WarningMessage from 'components/warning-message';
import {
  PaymentMethod as PaymentMethodEnum,
  useSaveDeliveryAddressMutation,
} from 'generated/graphql-gateway';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import { useKeyboardState } from 'hooks/use-keyboard-state';
import { ThreeDsMethod } from 'hooks/use-threeDS';
import { useAuthContext } from 'state/auth';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  SupportedCardType,
  defaultPaymentFieldVariation,
  defaultSupportedCardTypes,
} from 'state/launchdarkly/variations';
import { useLogger } from 'state/logger';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getServerOrderSubtotalBeforeOfferDiscounts } from 'state/loyalty/hooks/utils/helpers';
import { useMParticleContext } from 'state/mParticle';
import { useOrderContext } from 'state/order';
import { useCartEntriesDayParts } from 'state/order/hooks/use-cart-entries-day-parts';
import { useOrderTimedFire } from 'state/order/hooks/use-order-timed-fire';
import { usePaymentContext } from 'state/payment';
import { ThreeDSChallengeError, ThreeDSMethodError } from 'state/payment/hooks/errors';
import { ThreeDSType } from 'state/payment/hooks/types';
import { CardType } from 'state/payment/types';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { getConfigValue } from 'utils/environment';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { mapPaymentMethodCardTypes } from 'utils/payment/map-payment-method-card-type';
import { checkServiceModeUnavailable } from 'utils/restaurant';
import { relativeRoutes, routes } from 'utils/routing';
import { getLocalizedServiceMode } from 'utils/service-mode';
import { toast } from 'utils/toast';

import { PlaceOrderButton } from '../../place-order-button';
import SectionHeading from '../../section-heading';
import DeliveryCancellationPolicy from '../delivery-cancellation-policy';
import { getOrderProcessingText } from '../get-order-processing-text';
import PaymentSelection from '../payment-selection';
import PrepaidMethodReload from '../prepaid-method-reload';
import PrepaidTransferBalance from '../prepaid-transfer-balance';
import {
  DisclaimerCardOfferInvalid,
  DisclaimerCatering,
  DisclaimerWrapper,
  ErrorContainer,
  ErrorMessage,
  PaymentSection,
  WarningContainer,
} from '../styled';
import { OrderPaymentProps } from '../types';
import { logCartContinueOrder } from '../utils';

import Totals from './Totals';
import { StyledFullPageScreen } from './styled';
import { useOrderPayment } from './use-order-payment';
import { getNativePaymentDisclaimer } from './utils';

export const Payment: React.FC<OrderPaymentProps> = ({
  serverOrder,
  cartEntries,
  onPriceOrder,
  pricedAndAvailable,
}) => {
  const { isKeyboardOpen, keyboardHeight } = useKeyboardState();
  const [submitting, setSubmitting] = useState(false);
  const newPaymentValueIdRef = useRef('');

  const { formatMessage } = useIntl();
  const { serviceMode, isDelivery, isCurbside, isTableService } = useServiceModeContext();
  const { store } = useStoreContext();
  const order = useOrderContext();
  const logger = useLogger();
  const {
    addPaymentMethod,
    canUseApplePay,
    canUseGooglePay,
    checkoutPaymentMethodId,
    deletePaymentMethod,
    loading,
    setCheckoutPaymentMethodId,
    threeDS: paymentThreeDS,
    transformPaymentValues,
  } = usePaymentContext();

  const { cartEntriesActiveDayParts } = useCartEntriesDayParts(cartEntries);

  const { enableOrderTimedFire } = useOrderTimedFire({
    serverOrder,
    cartEntriesActiveDayParts,
  });

  const { getErrorMessages, getLoyaltyPaymentErrorMessages } = useGetPaymentErrors();
  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);
  const [hasAddPaymentMethodError, setAddPaymentMethodError] = useState(false);
  const [adyenIdealBank, setAdyenIdealBank] = useState('');
  const { loyaltyUser } = useLoyaltyUser();
  const { evaluateLoyaltyUserIncentives } = useLoyaltyContext();
  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedLoyaltyOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);
  const {
    cardIsValid,
    confirmStoreOnCheckout,
    dismissStoreConfirmationDialog,
    enableDelivery,
    enableImCloseDisplay,
    ErrorDialog,
    errors,
    handleAdyenInputChange,
    handleAdyenBrandDetected,
    handleAdyenInputErrors,
    setIsHandlingOrderCommit,
    handlePaymentMethodSelected,
    isApplePayLoading,
    isAdyenIdealMethodSelected,
    isPaycometSodexoMethodSelected,
    isPaycometChequeGourmetMethodSelected,
    isCashPayment,
    isCateringOrder,
    isGooglePayLoading,
    isOfferPaymentCardValid,
    isOrderPriceLoading,
    isPlacingOrder,
    isValidReloadPaymentMethodWithCard,
    isValidTip,
    needReloadAgain,
    offerIsValid,
    onChange,
    openServiceModeUnavailableErrorDialog,
    openStoreConfirmationModal,
    paymentMethods,
    paymentValues,
    placeOrderAriaLabel,
    reloadAmount,
    reloadPaymentValues,
    ServiceModeUnavailableErrorDialog,
    setCommitOrderAfterPricing,
    setIsAdyenIdealMethodSelected,
    setReloadPaymentValues,
    setShowAddPaymentMethod,
    setThreeDSChallengeTokenResponse,
    shouldConfirmStoreOnCheckout,
    showAddPaymentMethod,
    showOrderProcessingDisclaimer,
    showPaymentMethodSelection,
    showPrepaidTransferBalance,
    showReloadPaymentMethods,
    StoreConfirmationDialog,
    validateCreditCardForm,
    threeDS: orderThreeDS,
    threeDSChallengeTokenResponse,
    isAddGiftCardMethodSelected,
    setIsAddGiftCardMethodSelected,
  } = useOrderPayment({
    serverOrder,
    onPriceOrder,
    enableOrderTimedFire,
  });

  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);
  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const enableNewSaveAddress = useFlag(LaunchDarklyFlag.ENABLE_SAVE_DELIVERY_ADDRESS);
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];
  const enableSaveCard = !useFlag(LaunchDarklyFlag.HIDE_SAVE_CARD);

  const nativePaymentDisclaimer = getNativePaymentDisclaimer(
    canUseApplePay,
    canUseGooglePay,
    formatMessage
  );
  const paymentErrorMessages = getErrorMessages({
    invalidOfferError: !offerIsValid,
    invalidPaymentMethodError:
      !cardIsValid ||
      !isValidReloadPaymentMethodWithCard ||
      !isOfferPaymentCardValid ||
      hasAddPaymentMethodError,
    invalidTipAmountError: !isValidTip,
    pricedAndAvailableError: !pricedAndAvailable,
  });

  const maybeCommitOrderAfterPricing = useCallback(() => {
    dismissStoreConfirmationDialog();
    shouldConfirmStoreOnCheckout(true);
    if (isCurbside && onPriceOrder) {
      setCommitOrderAfterPricing(true);
      return onPriceOrder({
        customerName: `CS-${LocalStorage.getItem(StorageKeys.VEHICLE_DESCRIPTION)}`,
      });
    }
    if (isTableService && onPriceOrder) {
      setCommitOrderAfterPricing(true);
      return onPriceOrder({
        customerName: `TABLE-${LocalStorage.getItem(StorageKeys.TABLE_NUMBER)}`,
      });
    }
    setIsHandlingOrderCommit(true);
  }, [
    dismissStoreConfirmationDialog,
    isCurbside,
    isTableService,
    onPriceOrder,
    setCommitOrderAfterPricing,
    setIsHandlingOrderCommit,
    shouldConfirmStoreOnCheckout,
  ]);

  const handleAddPaymentMethod = useCallback(async () => {
    try {
      setAddPaymentMethodError(false);
      const newPaymentValueId = await addPaymentMethod(
        transformPaymentValues({
          paymentValues,
          paymentFieldVariations,
        }),
        {
          skipErrorDialogOnError: true,
        },
        paymentThreeDS.threeDSChallengeTokenResponse
      );
      setCheckoutPaymentMethodId(newPaymentValueId);
      handlePaymentMethodSelected(newPaymentValueId);
      return newPaymentValueId;
    } catch (error) {
      const isThreeDSError =
        error instanceof ThreeDSMethodError || error instanceof ThreeDSChallengeError;
      if (!isThreeDSError) {
        setAddPaymentMethodError(true);
        setIsCheckoutErrorsModalOpen(true);
      }
      return null;
    }
  }, [
    addPaymentMethod,
    handlePaymentMethodSelected,
    paymentFieldVariations,
    paymentThreeDS.threeDSChallengeTokenResponse,
    paymentValues,
    setCheckoutPaymentMethodId,
    setIsCheckoutErrorsModalOpen,
    transformPaymentValues,
  ]);

  const { isCartTotalNegative = false } = order?.calculateCartTotalWithDiscount?.() || {};
  const positiveCartTotal = isCartTotalNegative
    ? 0
    : getServerOrderSubtotalBeforeOfferDiscounts(serverOrder);

  const { refreshCurrentUser } = useAuthContext();
  const [saveDeliveryAddressMutation] = useSaveDeliveryAddressMutation({
    onCompleted: () => {
      refreshCurrentUser();
    },
    onError: () => {
      refreshCurrentUser();
    },
  });

  const updateSavedAddress = useCallback(async () => {
    const { savedDeliveryAddress } = order;
    if (savedDeliveryAddress && savedDeliveryAddress.address.location) {
      try {
        await saveDeliveryAddressMutation({
          variables: {
            input: {
              addressLine1: savedDeliveryAddress.address.addressLine1,
              addressLine2: savedDeliveryAddress.address.addressLine2,
              city: savedDeliveryAddress.address.city,
              country: IsoCountryCode[savedDeliveryAddress.address.country || ''],
              latitude: savedDeliveryAddress.coordinates.lat,
              phoneNumber: savedDeliveryAddress.address.phoneNumber,
              longitude: savedDeliveryAddress.coordinates.lng,
              state: savedDeliveryAddress.address.state,
              route: savedDeliveryAddress.address.route,
              streetNumber: savedDeliveryAddress.address.streetNumber,
              zip: savedDeliveryAddress.address.zip,
              alias: savedDeliveryAddress.address.location,
            },
          },
        });
      } catch (error) {
        logger.error(error);
      }
    }
  }, [logger, order, saveDeliveryAddressMutation]);

  const handleOnSubmit = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      if (enableNewSaveAddress) {
        updateSavedAddress();
      }
      if (event) {
        event.preventDefault();
      }

      if (showAddPaymentMethod && !validateCreditCardForm()) {
        return;
      }

      if (
        showAddPaymentMethod &&
        validateCreditCardForm() &&
        !checkoutPaymentMethodId &&
        paymentValues.saveCard
      ) {
        const paymentId = await handleAddPaymentMethod();

        if (!paymentId) {
          return;
        }

        newPaymentValueIdRef.current = paymentId;
      }
      const loyaltyId = loyaltyUser?.id;
      if (loyaltyId && showAddPaymentMethod && appliedLoyaltyOffers.length) {
        const paymentMethod = PaymentMethodEnum[paymentValues.cardType];

        await evaluateLoyaltyUserIncentives(
          loyaltyId,
          appliedLoyaltyOffers,
          order.cartEntries,
          appliedRewards,
          positiveCartTotal,
          paymentMethod
        );
      }

      if (!isCateringOrder && checkServiceModeUnavailable(order.serviceMode, store)) {
        openServiceModeUnavailableErrorDialog({
          message: formatMessage(
            { id: 'currentServiceModeUnavailable' },
            {
              serviceMode: getLocalizedServiceMode(order.serviceMode, formatMessage),
            }
          ),
        });
        return;
      }

      setSubmitting(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      showAddPaymentMethod,
      validateCreditCardForm,
      checkoutPaymentMethodId,
      paymentValues.saveCard,
      paymentValues.cardType,
      loyaltyUser,
      appliedLoyaltyOffers,
      isCateringOrder,
      order.serviceMode,
      order.cartEntries,
      store,
      handleAddPaymentMethod,
      evaluateLoyaltyUserIncentives,
      appliedRewards,
      positiveCartTotal,
      openServiceModeUnavailableErrorDialog,
      formatMessage,
    ]
  );
  const loyaltyPaymentMethodErrors = getLoyaltyPaymentErrorMessages();
  const errorMessages = [...paymentErrorMessages, ...loyaltyPaymentMethodErrors];
  useEffect(() => {
    if (!submitting) {
      return;
    }

    const hasErrors = !!errorMessages.length;

    if (hasErrors) {
      setIsCheckoutErrorsModalOpen(true);
    } else if (!isDelivery && confirmStoreOnCheckout) {
      openStoreConfirmationModal();
    } else {
      maybeCommitOrderAfterPricing();
    }

    setSubmitting(false);
  }, [
    confirmStoreOnCheckout,
    deletePaymentMethod,
    errorMessages.length,
    getLoyaltyPaymentErrorMessages,
    isDelivery,
    maybeCommitOrderAfterPricing,
    openStoreConfirmationModal,
    paymentErrorMessages.length,
    paymentValues.saveCard,
    submitting,
  ]);

  /**
   * ThreeDS Add Payment Method Flow
   */

  const paymentThreeDSMethodFlow = useMemo(
    () => ({
      transactionId: paymentThreeDS.transactionId,
      iframeContent: paymentThreeDS.iframeContent,
      activeType: paymentThreeDS.activeFlow,
    }),
    [paymentThreeDS.transactionId, paymentThreeDS.iframeContent, paymentThreeDS.activeFlow]
  );

  const onPayment3dsMethodComplete = useCallback(() => {
    const isThreeDSMethodFlow = paymentThreeDS.isThreeDSMethodFlow();
    if (isThreeDSMethodFlow) {
      handleOnSubmit();
    }
  }, [paymentThreeDS, handleOnSubmit]);

  const paymentThreeDSChallengeFlow = useMemo(
    () => ({
      activeType: paymentThreeDS.activeFlow,
      acsUrl: paymentThreeDS.acsUrl,
      challengeRequest: paymentThreeDS.challengeRequest,
    }),
    [paymentThreeDS.activeFlow, paymentThreeDS.acsUrl, paymentThreeDS.challengeRequest]
  );

  useEffect(() => {
    const isChallengeRequest = paymentThreeDS.isChallengeRequest();
    const isChallengeRequestComplete =
      isChallengeRequest && paymentThreeDS.threeDSChallengeTokenResponse;

    if (isChallengeRequestComplete) {
      handleOnSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentThreeDSChallengeFlow, paymentThreeDS.threeDSChallengeTokenResponse]);

  /**
   * ThreeDS Order Flow
   */
  const orderThreeDSMethodFlow = useMemo(
    () => ({
      transactionId: orderThreeDS.transactionId,
      iframeContent: orderThreeDS.iframeContent,
      activeType: orderThreeDS.activeFlow,
    }),
    [orderThreeDS.transactionId, orderThreeDS.iframeContent, orderThreeDS.activeFlow]
  );

  const onOrder3dsMethodComplete = useCallback(() => {
    const isThreeDSMethodFlow = orderThreeDS.isThreeDSMethodFlow();
    if (isThreeDSMethodFlow) {
      setIsHandlingOrderCommit(true);
    }
  }, [orderThreeDS, setIsHandlingOrderCommit]);

  const orderThreeDSChallengeFlow = useMemo(
    () => ({
      activeType: orderThreeDS.activeFlow,
      acsUrl: orderThreeDS.acsUrl,
      challengeRequest: orderThreeDS.challengeRequest,
    }),
    [orderThreeDS.activeFlow, orderThreeDS.acsUrl, orderThreeDS.challengeRequest]
  );

  useEffect(() => {
    const isChallengeRequestComplete =
      orderThreeDS.isChallengeRequest() && threeDSChallengeTokenResponse;

    if (isChallengeRequestComplete) {
      setIsHandlingOrderCommit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderThreeDSChallengeFlow, threeDSChallengeTokenResponse]);

  const selectedMethod = paymentMethods.find(
    method => method.accountIdentifier === checkoutPaymentMethodId
  );

  const handleGiftCardAdded = (fdAccountId: string) => {
    handlePaymentMethodSelected(fdAccountId);
  };

  const handleIdealBankSelection = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const bankCode = event.currentTarget.value;

    setAdyenIdealBank(bankCode);
  };

  const currentSelectedMethodType = getCurrentSelectedMethodType({
    isAddGiftCardMethodSelected,
    isAddNewCardMethodSelected: showAddPaymentMethod,
    isAdyenIdealMethodSelected,
    isPaycometSodexoMethodSelected,
    isPaycometChequeGourmetMethodSelected,
    selectedMethodExist: !!selectedMethod,
  });
  const isAddNewPaymentMethodTypeSelected =
    currentSelectedMethodType === CurrentSelectedMethodType.ADD_NEW_PAYMENT_METHOD;

  const isProcessingPayment =
    !!paymentThreeDSMethodFlow.iframeContent ||
    !!orderThreeDSMethodFlow.iframeContent ||
    loading ||
    isPlacingOrder ||
    isOrderPriceLoading ||
    isApplePayLoading ||
    isGooglePayLoading;

  const isPlaceOrderButtonDisabled =
    isProcessingPayment ||
    (!!selectedMethod?.prepaid && !enableGiftCard) ||
    isAddNewPaymentMethodTypeSelected;

  const mParticle = useMParticleContext();
  const handleContinueClick = () => {
    if (!(!confirmStoreOnCheckout || isDelivery)) {
      logCartContinueOrder(mParticle);
    }
  };

  const banks: IAdyenIdealBank[] = getConfigValue('adyen')?.ideal.idealBanks;

  return (
    <>
      <StyledFullPageScreen
        heading={formatMessage({ id: 'orderPayment' })}
        closeHref={routes.cart}
        disableClose={isProcessingPayment}
      >
        {isCheckoutErrorsModalOpen && (
          <CheckoutErrorsModal
            checkoutErrors={errorMessages}
            closeModal={() => {
              setIsCheckoutErrorsModalOpen(false);
            }}
          />
        )}
        <PaymentSection data-testid="cart-payment">
          <form onSubmit={handleOnSubmit} noValidate>
            <SectionHeading id="payment-method-header">
              {formatMessage({ id: 'paymentDetails' })}
            </SectionHeading>
            {/*TODO replace with auth.user && auth.user.fdCustomerId*/}
            <PaymentSelection>
              {showPaymentMethodSelection && (
                <PaymentMethod
                  aria-expanded="true"
                  aria-labelledby="payment-method-header"
                  methods={paymentMethods}
                  selectedMethodId={checkoutPaymentMethodId}
                  handleSelect={handlePaymentMethodSelected}
                  isAddCardOpen={showAddPaymentMethod}
                  isAdyenIdealMethodSelected={isAdyenIdealMethodSelected}
                  setIsAdyenIdealMethodSelected={setIsAdyenIdealMethodSelected}
                  isPaycometSodexoMethodSelected={isPaycometSodexoMethodSelected}
                  isPaycometChequeGourmetMethodSelected={isPaycometChequeGourmetMethodSelected}
                  openAddCardOnClick={setShowAddPaymentMethod}
                  isAddGiftCardMethodSelected={isAddGiftCardMethodSelected}
                  setIsAddGiftCardMethodSelected={setIsAddGiftCardMethodSelected}
                />
              )}

              {isAdyenIdealMethodSelected && (
                <BankSelect
                  banks={banks}
                  value={adyenIdealBank}
                  onChange={handleIdealBankSelection}
                  label={formatMessage({ id: 'bank' })}
                />
              )}

              {showAddPaymentMethod && (
                <CreditCardFormInputs
                  ariaLabel="credit-card-form-inputs"
                  isDelivery={isDelivery}
                  onChange={onChange}
                  onAdyenChange={handleAdyenInputChange}
                  onAdyenBrandDetected={handleAdyenBrandDetected}
                  onAdyenError={handleAdyenInputErrors}
                  paymentValues={paymentValues}
                  errors={errors}
                  withBorder={false}
                  withPadding={false}
                  supportedCardTypes={supportedCardTypes}
                  showSaveCard={enableSaveCard}
                />
              )}
              {/* Show PrepaidTransferBalance if the GC is selected  */}
              {showPrepaidTransferBalance && <PrepaidTransferBalance />}

              {showReloadPaymentMethods && !!paymentMethods.length && (
                <PrepaidMethodReload
                  reloadAmount={reloadAmount}
                  reloadPaymentValues={reloadPaymentValues}
                  setReloadPaymentValues={setReloadPaymentValues}
                  isDelivery={isDelivery}
                />
              )}
              {needReloadAgain && (
                <ErrorContainer>
                  <ErrorMessage>
                    {formatMessage({ id: 'insufficientBalanceReloadAgain' })}
                  </ErrorMessage>
                </ErrorContainer>
              )}
              {isCateringOrder && (
                <DisclaimerCatering data-testid="disclaimer-catering">
                  {`*${formatMessage({ id: 'cateringOrdersDisclaimer' })}`}
                </DisclaimerCatering>
              )}
              <Totals
                serverOrder={serverOrder}
                order={order}
                serviceMode={serviceMode}
                isDisabled={isPlaceOrderButtonDisabled}
              />
              {showOrderProcessingDisclaimer && (
                <DisclaimerWrapper tabIndex={0}>
                  {formatMessage({
                    id: getOrderProcessingText({
                      isCashPayment,
                      serviceMode: order.serviceMode,
                      enableImCloseDisplay,
                    }),
                  })}
                </DisclaimerWrapper>
              )}

              {!isOfferPaymentCardValid && (
                <DisclaimerCardOfferInvalid>
                  {formatMessage({ id: 'invalidPaymentCardTypeWithOffer' })}
                </DisclaimerCardOfferInvalid>
              )}
              {!selectedMethod && nativePaymentDisclaimer && (
                <WarningContainer>
                  <WarningMessage message={nativePaymentDisclaimer} />
                </WarningContainer>
              )}
              <PlaceOrderButton
                confirmation={!confirmStoreOnCheckout || isDelivery}
                isLoading={isProcessingPayment || evaluatingIncentives}
                disabled={isPlaceOrderButtonDisabled}
                margin={selectedMethod ? '1rem 0 0' : '0.5rem 0 0'}
                placeOrderAriaLabel={placeOrderAriaLabel}
                onClick={handleContinueClick}
              />
              {isDelivery && enableDelivery && <DeliveryCancellationPolicy />}
            </PaymentSelection>
          </form>
          <ErrorDialog />
          <ServiceModeUnavailableErrorDialog data-testid="servicemode-unavailable-error-modal" />
          <StoreConfirmationDialog onConfirm={maybeCommitOrderAfterPricing} />
          {paymentThreeDSMethodFlow.transactionId && paymentThreeDSMethodFlow.iframeContent && (
            <ThreeDsMethod
              iframeContent={paymentThreeDSMethodFlow.iframeContent}
              onComplete={onPayment3dsMethodComplete}
            />
          )}
          {paymentThreeDSChallengeFlow.activeType === ThreeDSType.CHALLENGE &&
            paymentThreeDSChallengeFlow.acsUrl &&
            paymentThreeDSChallengeFlow.challengeRequest && (
              <ThreeDSChallengeModal
                acsUrl={paymentThreeDSChallengeFlow.acsUrl}
                challengeRequestToken={paymentThreeDSChallengeFlow.challengeRequest}
                onChallengeSuccess={paymentThreeDS.setThreeDSChallengeTokenResponse}
                headerIdMessage={'addCreditCardVerification'}
              />
            )}
          {orderThreeDSMethodFlow.transactionId && orderThreeDSMethodFlow.iframeContent && (
            <ThreeDsMethod
              iframeContent={orderThreeDSMethodFlow.iframeContent}
              onComplete={onOrder3dsMethodComplete}
            />
          )}

          {orderThreeDSChallengeFlow.activeType === ThreeDSType.CHALLENGE &&
            orderThreeDSChallengeFlow.acsUrl &&
            orderThreeDSChallengeFlow.challengeRequest && (
              <ThreeDSChallengeModal
                acsUrl={orderThreeDSChallengeFlow.acsUrl}
                challengeRequestToken={orderThreeDSChallengeFlow.challengeRequest}
                onChallengeSuccess={setThreeDSChallengeTokenResponse}
                headerIdMessage={'paymentVerification'}
              />
            )}
        </PaymentSection>
        {isKeyboardOpen && <MobileSpacer height={keyboardHeight} />}
      </StyledFullPageScreen>
      <Routes>
        <Route
          path={relativeRoutes.transferGiftCard}
          element={
            <TransferGiftCardModal
              onCardTransfered={() =>
                toast.success(formatMessage({ id: 'transferBalanceCompletedMessage' }))
              }
            />
          }
        />
        <Route
          path={relativeRoutes.addGiftCard}
          element={
            <AddGiftCardModal
              handleSelect={handlePaymentMethodSelected}
              onCardAdded={handleGiftCardAdded}
            />
          }
        />
        <Route
          path={relativeRoutes.successTransferGiftCard}
          element={
            <SuccessTransferGiftCardModal
              onCardTransfered={() =>
                toast.success(formatMessage({ id: 'transferBalanceCompletedMessage' }))
              }
            />
          }
        />
        <Route path={relativeRoutes.successGiftCard} element={<SuccessGiftCardModal />} />
      </Routes>
    </>
  );
};
